import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Lock, LogOut, Mail, MessageSquare, Plus, RefreshCw, CheckCircle2, Loader2, ExternalLink, Shield, Pencil, Trash2, FolderKanban, X } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import SectionLabel from "../components/SectionLabel";
import { API } from "../lib/api";
import axios from "axios";

const STORAGE_KEY = "opr_admin_pw";

const adminApi = (password) =>
  axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json", "X-Admin-Password": password },
  });

export default function Admin() {
  const [password, setPassword] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);

  const verify = useCallback(async (pw) => {
    setChecking(true);
    try {
      await adminApi(pw).post("/admin/verify");
      sessionStorage.setItem(STORAGE_KEY, pw);
      setAuthed(true);
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
      setAuthed(false);
      if (pw) toast.error("Invalid admin password.");
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (password) verify(password);
  }, [password, verify]);

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPassword("");
    setAuthed(false);
  };

  if (!authed) {
    return <LoginScreen onSubmit={(pw) => { setPassword(pw); verify(pw); }} checking={checking} />;
  }

  return <Dashboard password={password} onLogout={logout} />;
}

function LoginScreen({ onSubmit, checking }) {
  const [pw, setPw] = useState("");
  return (
    <div data-testid="admin-login" className="min-h-screen bg-[#050505] text-white flex items-center justify-center pt-24 pb-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md mx-6 border border-white/10 bg-[#0a0a0c] p-8 lg:p-10"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 border border-[#3B82F6]/40 bg-[#3B82F6]/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#3B82F6]" />
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] text-[#3B82F6] uppercase">Admin</div>
            <div className="font-display text-xl font-semibold">Operexa Gennex Dashboard</div>
          </div>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); if (pw) onSubmit(pw); }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs uppercase tracking-wider">Admin Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid="admin-password-input"
                required
                autoFocus
                placeholder="Enter admin password"
                className="bg-black/40 border-white/10 rounded-none pl-10 focus-visible:ring-[#3B82F6]"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={checking || !pw}
            data-testid="admin-login-btn"
            className="w-full bg-[#3B82F6] hover:bg-[#60A5FA] rounded-none h-11 font-semibold"
          >
            {checking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : <>Access Dashboard</>}
          </Button>
          <p className="text-xs text-zinc-500 text-center">Restricted area. Authorized personnel only.</p>
        </form>
      </motion.div>
    </div>
  );
}

function Dashboard({ password, onLogout }) {
  const [tab, setTab] = useState("quotes");
  const tabs = [
    { id: "quotes", label: "Quote Requests", icon: MessageSquare },
    { id: "contacts", label: "Contact Inquiries", icon: Mail },
    { id: "projects", label: "Manage Projects", icon: FolderKanban },
    { id: "add-project", label: "Add Project", icon: Plus },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] text-white pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
          <div>
            <SectionLabel>Admin Dashboard</SectionLabel>
            <h1 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter mt-4">
              Operations Panel
            </h1>
          </div>
          <Button
            onClick={onLogout}
            data-testid="admin-logout-btn"
            variant="outline"
            className="rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white"
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>

        <div className="flex gap-px bg-white/5 border border-white/10 mb-8 overflow-x-auto">
          {tabs.map((t) => {
            const Ic = t.icon;
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  active ? "bg-[#3B82F6] text-white" : "bg-[#0a0a0c] text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <Ic className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "quotes" && <QuotesList password={password} />}
        {tab === "contacts" && <ContactsList password={password} />}
        {tab === "projects" && <ManageProjects password={password} />}
        {tab === "add-project" && <AddProject password={password} />}
      </div>
    </div>
  );
}

function QuotesList({ password }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi(password).get("/quote-requests");
      setItems(r.data || []);
    } catch {
      toast.error("Failed to load quote requests.");
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">
          {items.length} request{items.length === 1 ? "" : "s"}
        </div>
        <Button onClick={load} data-testid="quotes-refresh" variant="outline" size="sm" className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/5">
          <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh
        </Button>
      </div>
      {loading ? (
        <div className="py-20 text-center text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState label="No quote requests yet." />
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          {items.map((q) => (
            <div key={q.id} className="p-5 bg-[#0a0a0c] hover:bg-[#0f0f11] transition-colors" data-testid={`quote-row-${q.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="font-display text-lg font-semibold">{q.name}</div>
                  <a href={`mailto:${q.email}`} className="text-sm text-[#3B82F6] hover:underline">{q.email}</a>
                  {q.company && <span className="text-zinc-500 text-sm ml-2">· {q.company}</span>}
                  {q.phone && <span className="text-zinc-500 text-sm ml-2">· {q.phone}</span>}
                </div>
                <div className="flex items-center gap-2">
                  {q.project_slug && (
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase px-2 py-1 bg-[#3B82F6]/10 border border-[#3B82F6]/30 text-[#3B82F6]">
                      {q.project_slug}
                    </span>
                  )}
                  <span className="font-mono text-[10px] text-zinc-600">{formatDate(q.created_at)}</span>
                </div>
              </div>
              <p className="text-sm text-zinc-300 whitespace-pre-wrap">{q.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContactsList({ password }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi(password).get("/contacts");
      setItems(r.data || []);
    } catch {
      toast.error("Failed to load contact inquiries.");
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">
          {items.length} inquir{items.length === 1 ? "y" : "ies"}
        </div>
        <Button onClick={load} data-testid="contacts-refresh" variant="outline" size="sm" className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/5">
          <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh
        </Button>
      </div>
      {loading ? (
        <div className="py-20 text-center text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState label="No contact inquiries yet." />
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          {items.map((c) => (
            <div key={c.id} className="p-5 bg-[#0a0a0c] hover:bg-[#0f0f11] transition-colors" data-testid={`contact-row-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="font-display text-lg font-semibold">{c.first_name} {c.last_name}</div>
                  <a href={`mailto:${c.email}`} className="text-sm text-[#3B82F6] hover:underline">{c.email}</a>
                  {c.company && <span className="text-zinc-500 text-sm ml-2">· {c.company}</span>}
                  {c.phone && <span className="text-zinc-500 text-sm ml-2">· {c.phone}</span>}
                </div>
                <div className="flex items-center gap-2">
                  {c.preferred_datetime && (
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase px-2 py-1 bg-white/5 border border-white/10 text-zinc-300">
                      {c.preferred_datetime}
                    </span>
                  )}
                  <span className="font-mono text-[10px] text-zinc-600">{formatDate(c.created_at)}</span>
                </div>
              </div>
              <p className="text-sm text-zinc-300 whitespace-pre-wrap">{c.query}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddProject({ password }) {
  const [created, setCreated] = useState(null);
  return (
    <div className="border border-white/10 bg-[#0a0a0c] p-6 lg:p-10 max-w-3xl">
      {created && (
        <div className="mb-6 p-4 border border-[#3B82F6]/30 bg-[#3B82F6]/5 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-[#3B82F6]" />
          <div className="flex-1">
            <div className="font-semibold text-white">Created: {created.title}</div>
            <div className="text-xs text-zinc-500 font-mono">/{created.slug}</div>
          </div>
          <a href="/projects" target="_blank" rel="noopener noreferrer" className="text-sm text-[#3B82F6] hover:underline inline-flex items-center gap-1">
            View on site <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
      <ProjectForm
        password={password}
        mode="create"
        onSuccess={(p) => setCreated(p)}
        submitLabel={<><Plus className="w-4 h-4 mr-2" /> Create Project</>}
      />
    </div>
  );
}

function ManageProjects({ password }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects`);
      setProjects(r.data || []);
    } catch {
      toast.error("Failed to load projects.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (slug) => {
    setDeleting(slug);
    try {
      await adminApi(password).delete(`/projects/${slug}`);
      toast.success(`Project '${slug}' deleted.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">
          {projects.length} project{projects.length === 1 ? "" : "s"}
        </div>
        <Button onClick={load} data-testid="projects-refresh" variant="outline" size="sm" className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/5">
          <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-zinc-500">Loading…</div>
      ) : projects.length === 0 ? (
        <EmptyState label="No projects yet. Use the 'Add Project' tab." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.map((p) => (
            <div
              key={p.slug}
              data-testid={`manage-project-${p.slug}`}
              className="border border-white/10 bg-[#0a0a0c] overflow-hidden flex flex-col"
            >
              <div className="aspect-[16/9] overflow-hidden bg-black/40">
                {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-display text-lg font-semibold flex-1">{p.title}</h3>
                  {p.is_featured && (
                    <span className="font-mono text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 bg-[#3B82F6]/15 border border-[#3B82F6]/40 text-[#3B82F6]">
                      Featured
                    </span>
                  )}
                </div>
                <div className="font-mono text-[10px] text-zinc-500 mb-2">/{p.slug}</div>
                <p className="text-sm text-zinc-400 line-clamp-2 mb-3">{p.tagline}</p>
                <div className="text-sm text-white mb-4">{p.pricing_label}</div>
                <div className="mt-auto flex items-center gap-2 pt-3 border-t border-white/10">
                  <Button
                    onClick={() => setEditing(p)}
                    data-testid={`edit-${p.slug}`}
                    size="sm"
                    className="bg-[#3B82F6] hover:bg-[#60A5FA] rounded-none flex-1"
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button
                    onClick={() => {
                      if (window.confirm(`Delete project '${p.title}'? This cannot be undone.`)) {
                        handleDelete(p.slug);
                      }
                    }}
                    data-testid={`delete-${p.slug}`}
                    disabled={deleting === p.slug}
                    size="sm"
                    variant="outline"
                    className="rounded-none border-white/20 bg-transparent text-white hover:bg-red-500/10 hover:border-red-500/50 hover:text-red-400"
                  >
                    {deleting === p.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent
          data-testid="edit-project-dialog"
          className="bg-[#0a0a0c] border border-white/10 text-white max-w-3xl rounded-none p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
        >
          <div className="bg-[#050505] px-6 py-5 border-b border-white/10 sticky top-0 z-10">
            <div className="font-mono text-[10px] tracking-[0.25em] text-[#3B82F6] uppercase mb-1">Edit Project</div>
            <DialogHeader className="space-y-1">
              <DialogTitle className="font-display text-2xl text-white">{editing?.title}</DialogTitle>
              <DialogDescription className="text-zinc-400 text-sm">
                Update fields below and save changes. Slug must remain unique.
              </DialogDescription>
            </DialogHeader>
          </div>
          {editing && (
            <div className="p-6">
              <ProjectForm
                password={password}
                mode="edit"
                initialData={editing}
                onSuccess={() => {
                  setEditing(null);
                  load();
                }}
                onCancel={() => setEditing(null)}
                submitLabel={<><CheckCircle2 className="w-4 h-4 mr-2" /> Save Changes</>}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProjectForm({ password, mode, initialData, onSuccess, onCancel, submitLabel }) {
  const empty = {
    slug: "", title: "", tagline: "", description: "",
    image_url: "", pricing_label: "Starting from Rs. ",
    features: "", demo_url: "", is_featured: false,
  };

  const fromInitial = (d) => ({
    slug: d.slug || "",
    title: d.title || "",
    tagline: d.tagline || "",
    description: d.description || "",
    image_url: d.image_url || "",
    pricing_label: d.pricing_label || "",
    features: (d.features || []).join("\n"),
    demo_url: d.demo_url || "",
    is_featured: !!d.is_featured,
  });

  const [form, setForm] = useState(initialData ? fromInitial(initialData) : empty);
  const [submitting, setSubmitting] = useState(false);

  const h = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.slug || !form.title || !form.tagline || !form.description || !form.image_url || !form.pricing_label) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        ...form,
        features: form.features.split("\n").map((f) => f.trim()).filter(Boolean),
      };
      let r;
      if (mode === "edit") {
        r = await adminApi(password).put(`/projects/${initialData.slug}`, body);
        toast.success(`Project '${r.data.title}' updated.`);
      } else {
        r = await adminApi(password).post("/projects", body);
        toast.success(`Project '${r.data.title}' created.`);
        setForm(empty);
      }
      onSuccess?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save project.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <AdminField label="Slug *" hint="url-safe, e.g. my-project">
          <Input value={form.slug} onChange={h("slug")} required data-testid="project-slug" className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6]" />
        </AdminField>
        <AdminField label="Title *">
          <Input value={form.title} onChange={h("title")} required data-testid="project-title" className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6]" />
        </AdminField>
      </div>
      <AdminField label="Tagline *" hint="One short sentence">
        <Input value={form.tagline} onChange={h("tagline")} required data-testid="project-tagline" className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6]" />
      </AdminField>
      <AdminField label="Description *">
        <Textarea rows={4} value={form.description} onChange={h("description")} required data-testid="project-description" className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6]" />
      </AdminField>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <AdminField label="Image URL *">
          <Input value={form.image_url} onChange={h("image_url")} required data-testid="project-image" placeholder="https://..." className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6]" />
        </AdminField>
        <AdminField label="Demo URL">
          <Input value={form.demo_url} onChange={h("demo_url")} data-testid="project-demo" placeholder="https://..." className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6]" />
        </AdminField>
        <AdminField label="Pricing Label *" hint="e.g. Starting from Rs. 8,999">
          <Input value={form.pricing_label} onChange={h("pricing_label")} required data-testid="project-pricing" className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6]" />
        </AdminField>
        <AdminField label="Featured" hint="Show as main project card">
          <label className="flex items-center gap-2 mt-2 text-sm text-zinc-300">
            <input
              type="checkbox"
              checked={form.is_featured}
              onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
              data-testid="project-featured"
              className="w-4 h-4 accent-[#3B82F6]"
            />
            Mark as featured project
          </label>
        </AdminField>
      </div>
      <AdminField label="Features" hint="One per line">
        <Textarea
          rows={5}
          value={form.features}
          onChange={h("features")}
          data-testid="project-features"
          placeholder="Dynamic Page Builder&#10;Custom Domains&#10;AI Content Assistant"
          className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6] font-mono text-sm"
        />
      </AdminField>
      <div className="flex items-center gap-3 pt-2">
        <Button type="submit" disabled={submitting} data-testid="project-submit" className="bg-[#3B82F6] hover:bg-[#60A5FA] rounded-none h-11 font-semibold">
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : submitLabel}
        </Button>
        {onCancel && (
          <Button
            type="button"
            onClick={onCancel}
            variant="outline"
            data-testid="project-cancel"
            className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/5 h-11"
          >
            <X className="w-4 h-4 mr-2" /> Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

function AdminField({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <Label className="text-zinc-300 text-xs uppercase tracking-wider">{label}</Label>
        {hint && <span className="text-[10px] text-zinc-600">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="border border-dashed border-white/10 bg-[#0a0a0c] p-16 text-center">
      <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">{label}</div>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
